"use client";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";

interface DataItem {
  country: string;
  value: number;
}

const data: DataItem[] = [
  { country: "Indonesia", value: 37 },
  { country: "Thailand", value: 18 },
  { country: "Vietnam", value: 16 },
  { country: "Malaysia", value: 12 },
  { country: "Philippines", value: 8 },
  { country: "Myanmar", value: 3 },
  { country: "Singapore", value: 3 },
  { country: "Cambodia", value: 2 },
  { country: "Lao PDR", value: 1 },
  { country: "Brunei Darussalam", value: 1 },
];

const maxValue = Math.max(...data.map((d) => d.value));

const COLORS = {
  bar: "#7FAF3B",
  background: "#F4F4F4",
  border: "#CFCFCF",
  text: "#333333",
};

// 🔥 Responsive Hook
const useScreen = () => {
  const [screen, setScreen] = useState({
    isMobile: false,
    isTablet: false,
    isMediumScreen: false,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      setScreen({
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isMediumScreen: width < 1170,
      });
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screen;
};

// ✅ Responsive Centered Bar
const CenteredBar = (props: any) => {
  const { x, y, height, value, background } = props;

  const chartWidth = background?.width || 300;

  // Calculate width with a minimum to ensure text visibility
  let scaledWidth = (value / maxValue) * chartWidth * 0.9;
  // Minimum widths to ensure text visibility and hierarchy
  if (value === 1 && scaledWidth < 17) {
    scaledWidth = 16;
  } else if (value === 2 && scaledWidth < 25) {
    scaledWidth = 19;
  }

  const centeredX = x + chartWidth / 2 - scaledWidth / 2;

  return (
    <g>
      <rect
        x={centeredX}
        y={y}
        width={scaledWidth}
        height={height}
        fill={COLORS.bar}
        rx={3}
      />

      {/* responsive text */}
      <text
        x={centeredX + scaledWidth / 2}
        y={y + height / 2}
        fill="#fff"
        fontSize={height < 16 ? 10 : 12}
        fontWeight={600}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}%
      </text>
    </g>
  );
};

const InvertedPyramidChart: React.FC = () => {
  const { isMobile, isTablet, isMediumScreen } = useScreen();

  return (
    <div
      className="w-full flex flex-col justify-center bg-[#f9f9f9] p-4 overflow-hidden"
      style={{ height: isMediumScreen ? 420 : "100%" }}
    >
      <div
        style={{
          textAlign: "center",
          fontSize: isMobile ? 12 : 14,
          fontWeight: 600,
          marginBottom: 10,
          color: COLORS.text,
        }}
      >
        % Share of Total Final Energy Consumption (TFEC)
      </div>

      <ResponsiveContainer width="100%" height="90%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{
            top: 10,
            right: isMobile ? 10 : 20,
            left: isMobile ? 10 : 60, // 🔥 responsive left spacing
            bottom: 10,
          }}
          barSize={isMobile ? 16 : isTablet ? 16 : 18}
        >
          <XAxis type="number" hide domain={[0, maxValue]} />

          {/* 🔥 FIXED: Y-axis responsive + left aligned */}
          <YAxis
            type="category"
            dataKey="country"
            width={isMobile ? 100 : isTablet ? 130 : 160}
            tick={{
              fontSize: isMobile ? 10 : 12,
              fill: COLORS.text,
            }}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip cursor={false} />

          <Bar dataKey="value" shape={<CenteredBar />}>
            {data.map((_, i) => (
              <Cell key={i} fill={COLORS.bar} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default InvertedPyramidChart;
